import { ArchiveRestore, Disc3, Edit3, Play, Trash2 } from 'lucide-react';

import type { TrackDto } from '../api';
import { trackPath } from '../hooks/useRoute';
import { formatBytes, formatDuration } from './format';

export type LibraryLayout = 'list' | 'grid';

type LibraryGridProps = {
  tracks: TrackDto[];
  view: 'library' | 'trash';
  layout: LibraryLayout;
  signedIn: boolean;
  loading: boolean;
  playingId: string | null;
  /** Whether the loaded set is actually sounding, so the row marker can rest. */
  isPlaying: boolean;
  onPlay(track: TrackDto): void;
  onOpen(path: string): void;
  onEdit(track: TrackDto): void;
  onTrash(track: TrackDto): void;
  onRestore(track: TrackDto): void;
};

export function LibraryGrid({
  tracks,
  view,
  layout,
  signedIn,
  loading,
  playingId,
  isPlaying,
  onPlay,
  onOpen,
  onEdit,
  onTrash,
  onRestore,
}: LibraryGridProps) {
  if (tracks.length === 0 && loading) {
    return layout === 'list' ? <ListSkeleton /> : <GridSkeleton />;
  }

  if (tracks.length === 0) {
    return <EmptyState view={view} signedIn={signedIn} />;
  }

  /** Per-track handler binding, identical for both layouts. */
  const bind = (track: TrackDto) => ({
    track,
    view,
    onPlay: () => onPlay(track),
    onOpen: () => onOpen(trackPath(track.id)),
    onEdit: () => onEdit(track),
    onTrash: () => onTrash(track),
    onRestore: () => onRestore(track),
  });

  if (layout === 'list') {
    return (
      <div className="track-list">
        {tracks.map((track, index) => (
          <TrackRow
            key={track.id}
            index={index + 1}
            isLoaded={playingId === track.id}
            isSounding={playingId === track.id && isPlaying}
            {...bind(track)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="track-grid">
      {tracks.map((track) => (
        <TrackCard key={track.id} isPlaying={playingId === track.id} {...bind(track)} />
      ))}
    </div>
  );
}

/** True when the set can actually be started from the library. */
function isPlayable(track: TrackDto, view: 'library' | 'trash') {
  return track.status === 'ready' && view !== 'trash';
}

/**
 * The looping equaliser marking the loaded set, in place of its index.
 *
 * Decorative: the row already carries the same state in its accent title, so this is
 * hidden from assistive tech rather than announced twice.
 */
function NowPlayingBars({ sounding }: { sounding: boolean }) {
  return (
    <span className={`now-bars${sounding ? '' : ' is-paused'}`} aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function TrackRow({
  track,
  index,
  view,
  isLoaded,
  isSounding,
  onPlay,
  onOpen,
  onEdit,
  onTrash,
  onRestore,
}: {
  track: TrackDto;
  index: number;
  view: 'library' | 'trash';
  isLoaded: boolean;
  isSounding: boolean;
  onPlay(): void;
  onOpen(): void;
  onEdit(): void;
  onTrash(): void;
  onRestore(): void;
}) {
  const playable = isPlayable(track, view);

  return (
    <article className={`track-row${isLoaded ? ' is-playing' : ''}`}>
      <span className="row-lead">
        {isLoaded ? (
          <NowPlayingBars sounding={isSounding} />
        ) : (
          <>
            <span className="row-index numeric">{index}</span>
            <button
              type="button"
              className="row-play"
              disabled={!playable}
              onClick={onPlay}
              aria-label={`Play ${track.title}`}
            >
              <Play size={14} fill="currentColor" />
            </button>
          </>
        )}
      </span>

      <span className="row-cover">
        {track.coverUrl ? <img src={track.coverUrl} alt="" loading="lazy" /> : <Disc3 size={16} />}
      </span>

      <span className="row-main">
        <a
          className="row-title"
          href={trackPath(track.id)}
          onClick={(event) => {
            event.preventDefault();
            onOpen();
          }}
        >
          {track.title}
        </a>
        <span className="row-artist">{track.artist || 'Unknown artist'}</span>
        {track.uploader && (
          <span className="row-uploader">
            {track.uploader.displayUsername ?? track.uploader.username}
          </span>
        )}
      </span>

      <span className="row-meta numeric">
        {track.genre && <span className="row-genre">{track.genre}</span>}
        {track.status !== 'ready' && (
          <span className={`status ${track.status}`}>{track.status}</span>
        )}
        <span className="row-size">{formatBytes(track.sizeBytes)}</span>
        <span className="row-duration">{formatDuration(track.durationSeconds)}</span>
      </span>

      <span className="row-actions">
        {track.canManage &&
          (view === 'trash' ? (
            <button type="button" onClick={onRestore} aria-label={`Restore ${track.title}`}>
              <ArchiveRestore size={15} />
            </button>
          ) : (
            <>
              <button type="button" onClick={onEdit} aria-label={`Edit ${track.title}`}>
                <Edit3 size={15} />
              </button>
              <button
                type="button"
                className="danger"
                onClick={onTrash}
                aria-label={`Move ${track.title} to trash`}
              >
                <Trash2 size={15} />
              </button>
            </>
          ))}
      </span>
    </article>
  );
}

function TrackCard({
  track,
  view,
  isPlaying,
  onPlay,
  onOpen,
  onEdit,
  onTrash,
  onRestore,
}: {
  track: TrackDto;
  view: 'library' | 'trash';
  isPlaying: boolean;
  onPlay(): void;
  onOpen(): void;
  onEdit(): void;
  onTrash(): void;
  onRestore(): void;
}) {
  const playable = isPlayable(track, view);

  return (
    <article className={`track-card${isPlaying ? ' is-playing' : ''}`}>
      <div className="track-cover">
        {track.coverUrl ? (
          <img src={track.coverUrl} alt="" loading="lazy" />
        ) : (
          <div className="track-cover-empty">
            <Disc3 />
          </div>
        )}
        <button
          type="button"
          className="play-overlay"
          disabled={!playable}
          onClick={onPlay}
          aria-label={`Play ${track.title}`}
        >
          <span>
            <Play size={19} fill="currentColor" />
          </span>
        </button>
      </div>

      <div className="track-body">
        <h2>
          <a
            href={trackPath(track.id)}
            style={{ color: 'inherit', textDecoration: 'none' }}
            onClick={(event) => {
              event.preventDefault();
              onOpen();
            }}
          >
            {track.title}
          </a>
        </h2>
        <p className="track-artist">{track.artist || 'Unknown artist'}</p>
        {track.uploader && (
          <p className="row-uploader">
            {track.uploader.displayUsername ?? track.uploader.username}
          </p>
        )}
        <div className="track-meta numeric">
          <span>{formatDuration(track.durationSeconds)}</span>
          <span>{formatBytes(track.sizeBytes)}</span>
          {track.genre && <span>{track.genre}</span>}
          {track.status !== 'ready' && (
            <span className={`status ${track.status}`}>{track.status}</span>
          )}
        </div>
        {track.processingError && <p className="track-error">{track.processingError}</p>}
      </div>

      {track.canManage && (
        <div className="track-actions">
          {view === 'trash' ? (
            <button type="button" onClick={onRestore} aria-label={`Restore ${track.title}`}>
              <ArchiveRestore size={14} /> <span>Restore</span>
            </button>
          ) : (
            <>
              <button type="button" onClick={onEdit} aria-label={`Edit ${track.title}`}>
                <Edit3 size={14} /> <span>Edit</span>
              </button>
              <button
                type="button"
                className="danger"
                onClick={onTrash}
                aria-label={`Move ${track.title} to trash`}
              >
                <Trash2 size={14} /> <span>Trash</span>
              </button>
            </>
          )}
        </div>
      )}
    </article>
  );
}

function GridSkeleton() {
  return (
    <div className="track-grid">
      {[0, 1, 2, 3].map((key) => (
        <div className="skeleton" key={key} aria-hidden="true">
          <div className="skeleton-cover" />
          <div className="skeleton-lines">
            <i />
            <i />
            <i />
          </div>
        </div>
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="track-list list-skeleton" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((key) => (
        <div className="track-row" key={key}>
          <span className="row-lead" />
          <span className="skeleton-cover" />
          <span className="row-main skeleton-lines">
            <i />
            <i />
          </span>
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ view, signedIn }: { view: 'library' | 'trash'; signedIn: boolean }) {
  return (
    <div className="empty-state">
      <div className="dither-block" aria-hidden="true" />
      <h2>{view === 'library' ? 'Nothing here yet' : 'Trash is empty'}</h2>
      <p>
        {view === 'trash'
          ? 'Deleted sets stay recoverable for seven days before they are purged.'
          : signedIn
            ? 'Upload an MP3 from the owner menu to start the archive.'
            : 'No sets have been published yet. Check back soon.'}
      </p>
    </div>
  );
}
